import { getApps, initializeApp } from "firebase/app";
import { browserLocalPersistence, browserSessionPersistence, connectAuthEmulator, indexedDBLocalPersistence, initializeAuth, type Auth } from "firebase/auth";
import type { PublicConfig } from "./types";

let authInstance: Auth | null = null;

export function initFirebaseAuth(config: PublicConfig): Auth {
  if (authInstance) return authInstance;
  const app =
    getApps()[0] ??
    initializeApp({
      apiKey: config.firebase.apiKey,
      authDomain: config.firebase.authDomain,
      projectId: config.firebase.projectId,
      ...(config.firebase.appId ? { appId: config.firebase.appId } : {}),
    });
  // Email/password auth needs persistence, but no OAuth popup/redirect iframe. On mobile,
  // getAuth's default resolver preloads Google scripts that this app's CSP intentionally excludes.
  const auth = initializeAuth(app, {
    persistence: [indexedDBLocalPersistence, browserLocalPersistence, browserSessionPersistence],
  });
  if (config.authEmulatorUrl) connectAuthEmulator(auth, config.authEmulatorUrl, { disableWarnings: true });
  auth.useDeviceLanguage();
  authInstance = auth;
  return auth;
}

/** Friendly copy for Firebase Auth error codes (never echo raw SDK text). */
export function authErrorMessage(error: unknown): string {
  const code = typeof error === "object" && error && "code" in error ? String((error as { code: string }).code) : "";
  switch (code) {
    case "auth/invalid-credential":
    case "auth/wrong-password":
    case "auth/user-not-found":
    case "auth/invalid-login-credentials":
      return "That email and password don't match an account. Check them and try again.";
    case "auth/email-already-in-use":
      return "An account already uses this email. Sign in instead, or reset your password.";
    case "auth/invalid-email":
      return "Enter a valid email address.";
    case "auth/weak-password":
    case "auth/password-does-not-meet-requirements":
      return "Choose a stronger password — at least 8 characters.";
    case "auth/too-many-requests":
      return "Too many attempts. Wait a few minutes, then try again.";
    case "auth/network-request-failed":
      return "Couldn't reach the sign-in service. Check your connection and try again.";
    case "auth/expired-action-code":
      return "This link has expired. Request a new one.";
    case "auth/invalid-action-code":
      return "This link is invalid or has already been used. Request a new one.";
    case "auth/user-disabled":
      return "This account has been disabled.";
    case "auth/requires-recent-login":
      return "For your security, sign in again before doing this.";
    default:
      return "Something went wrong with sign-in. Try again.";
  }
}
